// ════════════════════════════════════════════════════════════════════════════
// 👆 SwipeableRow — Phase 2.2 Swipe-to-delete cart row wrapper
// ════════════════════════════════════════════════════════════════════════════
// Preserves the existing trash-button UX while adding horizontal swipe:
//   - Swipe left > 100px → fires onDelete()
//   - Otherwise snaps back to origin
//   - `dragDirectionLock` ensures vertical scroll still works
//   - Red "Release to delete" indicator reveals as user swipes
// ════════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { motion, PanInfo } from 'motion/react';
import { Trash2 } from 'lucide-react';
import { Haptics } from '../../utils/haptics';

interface SwipeableRowProps {
  onDelete: () => void;
  className?: string;
  threshold?: number;
  children: React.ReactNode;
}

export const SwipeableRow: React.FC<SwipeableRowProps> = ({
  onDelete,
  className = '',
  threshold = 100,
  children,
}) => {
  const [triggered, setTriggered] = useState(false);

  const onDragEnd = (_: any, info: PanInfo) => {
    if (info.offset.x <= -threshold) {
      Haptics.fire('warn');
      onDelete();
    }
    setTriggered(false);
  };

  const onDrag = (_: any, info: PanInfo) => {
    const shouldTrigger = info.offset.x <= -threshold;
    if (shouldTrigger !== triggered) {
      setTriggered(shouldTrigger);
      if (shouldTrigger) Haptics.fire('tap');
    }
  };

  return (
    <div className={`relative overflow-hidden rounded-xl mb-2 ${className}`}>
      {/* Red danger backdrop revealed during swipe */}
      <div
        className={`absolute inset-0 flex items-center justify-end px-4 rounded-xl transition-colors ${
          triggered ? 'bg-rose-500' : 'bg-rose-400'
        }`}
        aria-hidden
      >
        <div className="flex items-center gap-1.5 text-white font-black text-[11px] uppercase tracking-widest">
          <Trash2 size={14} />
          <span>{triggered ? 'Release' : 'Swipe'}</span>
        </div>
      </div>
      {/* Draggable foreground */}
      <motion.div
        drag="x"
        dragDirectionLock
        dragConstraints={{ left: -200, right: 0 }}
        dragElastic={{ left: 0.35, right: 0 }}
        dragSnapToOrigin
        onDrag={onDrag}
        onDragEnd={onDragEnd}
        className="relative touch-pan-y"
      >
        {children}
      </motion.div>
    </div>
  );
};
